import React, { useContext } from "react";
import Sidebar from "../components/Sidebar";
import DocSearchContext from "../context/DocSearchContext";
import SearchContext from "../context/SearchContext";
import { Route, Switch, useLocation } from "react-router-dom";
import AuthGuard from "../auth/AuthGuard";
import routes from "../routes";
import PublicGuard from "../auth/PublicGuard ";
import AutoLogoutTimer from "../services/idleTimer";
import { ContextApi } from "../App";
import TrfSearchContext from "../context/TrfSearchContext";
import NotificationContext from "../context/NotificationContext";
import PermissionGuard from "../auth/PermissionGuard";

const publicRoutes = {
  "/": "user",
  "/careers": "candidate",
  "/consultant": "consultant",
};

const normalizePathname = (pathname) => pathname.replace(/\/+$/, "") || "/";

function Layout() {
  const { isMenuExpanded, setIsMenuExpanded } = useContext(ContextApi);
  const location = useLocation();
  const { pathname } = location;
  const normalizedPathname = normalizePathname(pathname);
  const isCandidateRoute =
    normalizedPathname === "/careers" ||
    normalizedPathname.startsWith("/careers/");
  const isPublicEntryRoute = Object.prototype.hasOwnProperty.call(
    publicRoutes,
    normalizedPathname,
  );

  const showSidebar = routes.some((route) => {
    const basePath = route.path.split("/:")[0];
    const isAuthenticated = !!localStorage.getItem(`${route.type || 'user'}.token`);
    return (
      route.isProtected && 
      route.showSidebar && 
      pathname.startsWith(basePath) &&
      isAuthenticated
    );
  });

  const routeContent = (
    <>
      {showSidebar && (
        <Sidebar
          isMenuExpanded={isMenuExpanded}
          setIsMenuExpanded={setIsMenuExpanded}
        />
      )}
      <Switch>
        {routes?.map((route, index) => {
          const Component = route.component;

          const renderComponent = (props) => {
            if (route.logoutTime) {
              return (
                <AutoLogoutTimer
                  type={route.type}
                  ComposedClass={Component}
                  {...props}
                />
              );
            }
            return <Component {...props} />;
          };

          // 1. Handle Public Routes first if they are in the special list
          if (publicRoutes[route.path]) {
            return (
              <Route
                key={index}
                path={route.path}
                exact
                render={(routerProps) => (
                  <PublicGuard type={publicRoutes[route.path] || ""}>
                    <Component {...routerProps} />
                  </PublicGuard>
                )}
              />
            );
          }

          // 2. Handle Protected Routes
          if (route.isProtected) {
            return (
              <Route
                key={index}
                path={route.path}
                exact
                render={(routerProps) => (
                  <AuthGuard type={route.type}>
                    {route.requiredPermission ? (
                      <PermissionGuard requiredPermission={route.requiredPermission}>
                        {renderComponent(routerProps)}
                      </PermissionGuard>
                    ) : (
                      renderComponent(routerProps)
                    )}
                  </AuthGuard>
                )}
              />
            );
          }

          // 3. Default case for other routes (isProtected: false)
          return (
            <Route
              key={index}
              path={route.path}
              exact
              render={(routerProps) => {
                if (route.requiredPermission) {
                  return (
                    <PermissionGuard requiredPermission={route.requiredPermission}>
                      {renderComponent(routerProps)}
                    </PermissionGuard>
                  );
                }
                return renderComponent(routerProps);
              }}
            />
          );
        })}
      </Switch>
    </>
  );

  if (isPublicEntryRoute || isCandidateRoute) {
    return routeContent;
  }

  return (
    <DocSearchContext>
      <SearchContext>
        <TrfSearchContext>
          <NotificationContext>
            {routeContent}
          </NotificationContext>
        </TrfSearchContext>
      </SearchContext>
    </DocSearchContext>
  );
}

export default Layout;

